import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Badge,
  Spinner,
  Table,
  Nav,
  Tab,
  Modal,
  Alert,
  Form,
} from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getTokenFromLocalStorage } from 'utilities/tokenUtils';
import { colors } from './partnerTheme';
import AppSelect from "../../components/ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL;

const PartnerDetails = () => {
  const history = useHistory();
  const { id } = useParams();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [riders, setRiders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showRidersModal, setShowRidersModal] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [ridersLoading, setRidersLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [unassigningOrderId, setUnassigningOrderId] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

  const showConfirm = (title, message, onConfirm) => {
    setConfirmModal({ show: true, title, message, onConfirm });
  };
  const hideConfirm = () => setConfirmModal({ show: false, title: '', message: '', onConfirm: null });

  useEffect(() => {
    fetchPartnerDetails();
  }, [id]);

  const fetchPartnerDetails = async () => {
    try {
      setLoading(true);
      const token = getTokenFromLocalStorage();

      const [partnerRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/api/3pl-partners/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/3pl-partners/${id}/analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { data: null } })),
      ]);
      setPartner(partnerRes.data.data?.partner || partnerRes.data.data);
      setAnalytics(analyticsRes.data.data);
    } catch (error) {
      console.error('Error fetching partner:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRiders = async () => {
    try {
      setRidersLoading(true);
      const token = getTokenFromLocalStorage();
      const response = await axios.get(`${API_URL}/api/3pl-partners/${id}/riders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRiders(response.data.data?.riders || []);
      setShowRidersModal(true);
    } catch (error) {
      console.error('Error fetching riders:', error);
      toast.error('Failed to load riders. Please try again.');
    } finally {
      setRidersLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setOrdersLoading(true);
      const token = getTokenFromLocalStorage();

      // First fetch partner's riders to get their IDs
      const ridersRes = await axios.get(`${API_URL}/api/3pl-partners/${id}/riders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const partnerRiders = ridersRes.data.data?.riders || [];

      if (partnerRiders.length === 0) {
        setOrders([]);
        setShowOrdersModal(true);
        return;
      }

      // Fetch all orders and filter by rider IDs client-side
      // (In production, you'd want a backend endpoint that filters by partnerId)
      const ordersRes = await axios.get(`${API_URL}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const riderIds = partnerRiders.map(r => r._id);
      const partnerOrders = (ordersRes.data.data || []).filter(order =>
        order.riderId && riderIds.includes(order.riderId._id || order.riderId)
      ).slice(0, 50); // Limit to 50 most recent

      setOrders(partnerOrders);
      setShowOrdersModal(true);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders. Please try again.');
    } finally {
      setOrdersLoading(false);
    }
  };

  const unassignOrder = async (order) => {
    hideConfirm();
    setUnassigningOrderId(order._id);
    try {
      const token = getTokenFromLocalStorage();
      const response = await axios.post(
        `${API_URL}/weborders/${order._id}/reassign`,
        { unassign: true, reason: 'Returned to dispatch queue by Lusuku staff' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders((current) => current.filter((item) => item._id !== order._id));
      toast.success(response.data?.data?.message || 'Rider unassigned');
    } catch (error) {
      toast.error(error.response?.data?.error?.message || error.response?.data?.message || 'Failed to unassign rider');
    } finally {
      setUnassigningOrderId(null);
    }
  };

  const confirmUnassignOrder = (order) => {
    const riderName = order.riderId?.fullName || order.riderId?.firstName || 'this rider';
    showConfirm(
      `Unassign ${riderName}?`,
      'This returns the order to the partner dispatch queue and immediately removes it from the rider app.',
      () => unassignOrder(order)
    );
  };

  const initiatePayout = async () => {
    const earnings = analytics?.totalEarnings || 0;
    if (earnings <= 0) {
      toast.warn('No earnings available for payout.');
      return;
    }

    showConfirm(
      'Initiate Payout',
      `Initiate payout of UGX ${earnings.toLocaleString()} to ${partner.companyName}?\n\nBank: ${partner.bankDetails?.bankName || 'Not configured'} — ${partner.bankDetails?.accountNumber || 'Not configured'}`,
      async () => {
        hideConfirm();
        try {
          setPayoutLoading(true);
          const token = getTokenFromLocalStorage();
          await axios.post(`${API_URL}/api/3pl-partners/${id}/payouts/initiate`, {
            amount: earnings,
            notes: `Payout initiated from dashboard on ${new Date().toLocaleDateString()}`
          }, {
            headers: { Authorization: `Bearer ${token}` },
          });
          toast.success('Payout initiated successfully!');
          fetchPartnerDetails();
        } catch (error) {
          toast.error(error.response?.data?.error?.message || 'Failed to initiate payout.');
        } finally {
          setPayoutLoading(false);
        }
      }
    );
  };

  const updatePartnerStatus = async (newStatus) => {
    showConfirm(
      'Update Status',
      `Change ${partner.companyName}'s status to "${newStatus}"?`,
      async () => {
        hideConfirm();
        try {
          setStatusUpdating(true);
          const token = getTokenFromLocalStorage();
          await axios.put(`${API_URL}/api/3pl-partners/${id}`, { status: newStatus }, {
            headers: { Authorization: `Bearer ${token}` },
          });
          toast.success(`Partner status updated to ${newStatus}.`);
          fetchPartnerDetails();
        } catch (error) {
          toast.error(error.response?.data?.error?.message || 'Failed to update status.');
        } finally {
          setStatusUpdating(false);
        }
      }
    );
  };

  const handleResendInvite = async () => {
    showConfirm(
      'Resend Activation Link',
      `Resend activation email to ${partner.email}?\n\nA new 48-hour link will be generated, invalidating the previous one.`,
      async () => {
        hideConfirm();
        try {
          setResendingInvite(true);
          const authToken = getTokenFromLocalStorage();
          const response = await axios.post(
            `${API_URL}/api/3pl-partners/${id}/resend-invite`,
            {},
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
          const data = response.data;
          if (data.data?.inviteEmailSent) {
            toast.success(`Activation email resent to ${partner.email}. Link expires in 48 hours.`);
          } else {
            // Email failed — show copyable link in a toast
            const rawToken = data.data?.activationToken;
            const url = `${window.location.origin}/auth/partner-activate?token=${rawToken}`;
            navigator.clipboard.writeText(url).catch(() => { });
            toast.warn(
              <div>
                <strong>Email delivery failed.</strong><br />
                Activation link copied to clipboard. Share it with the partner manually.
              </div>,
              { autoClose: 8000 }
            );
          }
        } catch (error) {
          toast.error(error.response?.data?.error?.message || 'Failed to resend invite.');
        } finally {
          setResendingInvite(false);
        }
      }
    );
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: { backgroundColor: colors.green, color: colors.white },
      pending: { backgroundColor: colors.amber, color: colors.white },
      suspended: { backgroundColor: colors.red, color: colors.white },
      inactive: { backgroundColor: colors.line, color: colors.body },
    };
    return (
      <Badge style={statusStyles[status] || statusStyles.inactive}>
        {status}
      </Badge>
    );
  };

  const getTierBadge = (tier) => {
    const tierStyles = {
      platinum: { backgroundColor: colors.blue, color: colors.white },
      gold: { backgroundColor: colors.amber, color: colors.white },
      silver: { backgroundColor: colors.line, color: colors.body },
      bronze: { backgroundColor: colors.teal, color: colors.white },
    };
    return (
      <Badge style={tierStyles[tier] || tierStyles.silver}>
        {tier}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="content text-center py-5">
        <Spinner animation="grow" style={{ color: "blue" }} />
        <p className="mt-2">Loading Partner Details...</p>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="content">
        <Card>
          <CardBody className="text-center py-5">
            <i className="nc-icon nc-simple-remove" style={{ fontSize: '3rem', color: colors.red }} />
            <h4 className="mt-3">Partner Not Found</h4>
            <Button
              style={{ backgroundColor: colors.blue, borderColor: colors.blue, color: colors.white }}
              onClick={() => history.push('/admin/3pl-partners')}
            >
              Back to Partners
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="content">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Button
            style={{
              backgroundColor: colors.lineSoft,
              borderColor: colors.line,
              color: colors.body
            }}
            onClick={() => history.goBack()}
          >
            <i className="nc-icon nc-minimal-left" /> Back
          </Button>
        </Col>
        <Col className="text-right d-flex align-items-center justify-content-end">
          {/* Quick Status Change */}
          <AppSelect block
            size="sm"
            value={partner.status}
            onChange={(e) => updatePartnerStatus(e.target.value)}
            disabled={statusUpdating}
            style={{
              width: '140px',
              marginRight: '10px',
              borderColor: partner.status === 'active' ? colors.green :
                partner.status === 'pending' ? colors.amber :
                  partner.status === 'suspended' ? colors.red : colors.line,
              fontWeight: 'bold'
            }}
          >
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Inactive</option>
          </AppSelect>
          {statusUpdating && <Spinner size="sm" animation="border" className="me-2" style={{ color: colors.blue }} />}
          <Button
            style={{
              backgroundColor: colors.amber,
              borderColor: colors.amber,
              color: colors.white
            }}
            onClick={() => history.push(`/admin/3pl-partners-edit/${id}`)}
          >
            <i className="nc-icon nc-settings-gear-64" /> Edit
          </Button>
        </Col>
      </Row>

      {/* Partner Info Card */}
      <Row>
        <Col md="8">
          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col>
                  <CardTitle tag="h4">{partner.companyName}</CardTitle>
                  <p className="card-category">
                    {getTierBadge(partner.partnershipTier)} {getStatusBadge(partner.status)}
                  </p>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Tab.Container defaultActiveKey="overview">
                <Nav variant="tabs" className="mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey="overview">Overview</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="contact">Contact</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="financial">Financial</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="areas">Service Areas</Nav.Link>
                  </Nav.Item>
                </Nav>

                <Tab.Content>
                  <Tab.Pane eventKey="overview">
                    <Row>
                      <Col md="6">
                        <p><strong>Registration Number:</strong> {partner.registrationNumber || 'N/A'}</p>
                        <p><strong>Tax ID:</strong> {partner.taxId || 'N/A'}</p>
                        <p><strong>Created:</strong> {new Date(partner.createdAt).toLocaleDateString()}</p>
                      </Col>
                      <Col md="6">
                        <p><strong>Active Riders:</strong> {partner.activeRiderCount || 0}</p>
                        <p><strong>Commission Rate:</strong> {partner.commissionRate || 40}%</p>
                        <p><strong>Base Delivery Fee:</strong> UGX {(partner.baseDeliveryFee || 5000).toLocaleString()}</p>
                      </Col>
                    </Row>
                  </Tab.Pane>

                  <Tab.Pane eventKey="contact">
                    <h4>Contact Person</h4>
                    <p><strong>Name:</strong> {partner.contactPerson?.name || 'N/A'}</p>
                    <p><strong>Position:</strong> {partner.contactPerson?.position || 'N/A'}</p>
                    <p><strong>Phone:</strong> {partner.contactPerson?.phone || 'N/A'}</p>
                    <p><strong>Email:</strong> {partner.contactPerson?.email || 'N/A'}</p>
                    <hr />
                    <h4>Address</h4>
                    <p>
                      {partner.address?.street && `${partner.address.street}, `}
                      {partner.address?.city && `${partner.address.city}, `}
                      {partner.address?.district && `${partner.address.district}, `}
                      {partner.address?.country || 'Uganda'}
                    </p>
                  </Tab.Pane>

                  <Tab.Pane eventKey="financial">
                    <h4>Bank Details</h4>
                    <p><strong>Bank:</strong> {partner.bankDetails?.bankName || 'N/A'}</p>
                    <p><strong>Account Name:</strong> {partner.bankDetails?.accountName || 'N/A'}</p>
                    <p><strong>Account Number:</strong> {partner.bankDetails?.accountNumber || 'N/A'}</p>
                    <p><strong>Branch Code:</strong> {partner.bankDetails?.branchCode || 'N/A'}</p>
                  </Tab.Pane>

                  <Tab.Pane eventKey="areas">
                    {partner.serviceAreas?.length > 0 ? (
                      <Table responsive size="sm">
                        <thead>
                          <tr>
                            <th>Area Name</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {partner.serviceAreas.map((area, idx) => (
                            <tr key={idx}>
                              <td>{area.name}</td>
                              <td>
                                <Badge style={{
                                  backgroundColor: area.isActive ? colors.green : colors.line,
                                  color: area.isActive ? colors.white : colors.body
                                }}>
                                  {area.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <p className="text-muted">No service areas configured</p>
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </CardBody>
          </Card>
        </Col>

        {/* Stats Sidebar */}
        <Col md="4">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="nc-icon nc-delivery-fast" style={{ color: colors.green }} />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Total Deliveries</p>
                    <CardTitle tag="p">
                      {analytics?.totalDeliveries || partner.performanceMetrics?.totalDeliveries || 0}
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="nc-icon nc-chart-bar-32" style={{ color: colors.blue }} />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Avg Rating</p>
                    <CardTitle tag="p">
                      {(partner.performanceMetrics?.averageRating || 0).toFixed(1)} / 5
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="icon-big text-center icon-warning">
                    <i className="nc-icon nc-money-coins" style={{ color: colors.amber }} />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Total Earnings</p>
                    <CardTitle tag="p">
                      UGX {(analytics?.totalEarnings || 0).toLocaleString()}
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h6>Quick Actions</h6>
              <div className="d-grid gap-2">
                <Button
                  style={{
                    backgroundColor: colors.white,
                    borderColor: colors.blue,
                    color: colors.blue
                  }}
                  size="sm"
                  onClick={fetchRiders}
                  disabled={ridersLoading}
                >
                  {ridersLoading ? (
                    <><Spinner size="sm" animation="border" className="me-1" /> Loading...</>
                  ) : (
                    <><i className="nc-icon nc-single-02" /> View Riders ({partner.activeRiderCount || 0})</>
                  )}
                </Button>
                <Button
                  style={{
                    backgroundColor: colors.white,
                    borderColor: colors.blue,
                    color: colors.blue
                  }}
                  size="sm"
                  onClick={fetchOrders}
                  disabled={ordersLoading}
                >
                  {ordersLoading ? (
                    <><Spinner size="sm" animation="border" className="me-1" /> Loading...</>
                  ) : (
                    <><i className="nc-icon nc-paper-2" /> View Orders</>
                  )}
                </Button>
                <Button
                  style={{
                    backgroundColor: colors.white,
                    borderColor: colors.green,
                    color: colors.green
                  }}
                  size="sm"
                  onClick={initiatePayout}
                  disabled={payoutLoading}
                >
                  {payoutLoading ? (
                    <><Spinner size="sm" animation="border" className="me-1" /> Processing...</>
                  ) : (
                    <><i className="nc-icon nc-money-coins" /> Initiate Payout</>
                  )}
                </Button>
                {/* Resend Invite — only shown for partners who haven't activated yet */}
                {!partner.isActivated && (
                  <>
                    <hr style={{ margin: '4px 0' }} />
                    <div style={{
                      background: '#FEF9C3', border: '1px solid #FDE68A',
                      borderRadius: 6, padding: '6px 10px', fontSize: 11,
                      color: '#92400E', marginBottom: 2
                    }}>
                      ⚠️ <strong>Account not activated.</strong> Partner hasn't set their password yet.
                    </div>
                    <Button
                      style={{
                        backgroundColor: colors.white,
                        borderColor: colors.amber,
                        color: colors.amber
                      }}
                      size="sm"
                      onClick={handleResendInvite}
                      disabled={resendingInvite}
                    >
                      {resendingInvite ? (
                        <><Spinner size="sm" animation="border" className="me-1" /> Sending...</>
                      ) : (
                        <><i className="nc-icon nc-email-83" /> Resend Activation Link</>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Riders Modal */}
      <Modal show={showRidersModal} onHide={() => setShowRidersModal(false)} size="lg">
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: colors.blue, color: colors.white }}>
          <Modal.Title>{partner?.companyName} - Riders</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {riders.length === 0 ? (
            <Alert>No riders found for this partner.</Alert>
          ) : (
            <Table responsive striped hover size="sm">
              <thead style={{ color: colors.blue }}>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Rating</th>
                  <th>Deliveries</th>
                </tr>
              </thead>
              <tbody>
                {riders.map((rider) => (
                  <tr key={rider._id}>
                    <td>{rider.fullName || rider.firstName + ' ' + rider.lastName}</td>
                    <td>{rider.phoneNumber}</td>
                    <td>
                      <Badge style={{
                        backgroundColor: rider.onlineStatus === 'Online' ? colors.green : colors.line,
                        color: rider.onlineStatus === 'Online' ? colors.white : colors.body
                      }}>
                        {rider.onlineStatus || 'Offline'}
                      </Badge>
                    </td>
                    <td>{(rider.performanceMetrics?.averageRating || 0).toFixed(1)} ⭐</td>
                    <td>{rider.performanceMetrics?.totalDeliveries || 0}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{ backgroundColor: colors.lineSoft, borderColor: colors.line, color: colors.body }}
            onClick={() => setShowRidersModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Orders Modal */}
      <Modal show={showOrdersModal} onHide={() => setShowOrdersModal(false)} size="lg">
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: colors.green, color: colors.white }}>
          <Modal.Title>{partner?.companyName} - Orders</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {orders.length === 0 ? (
            <Alert
            // variant="info"
            >No orders found for this partner's riders.</Alert>
          ) : (
            <Table responsive striped hover size="sm">
              <thead style={{ color: colors.blue }}>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Rider</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order._id}>
                    <td>#{order._id?.slice(-6).toUpperCase()}</td>
                    <td>{order.client_id?.fullName || order.shippingAddress?.name || 'N/A'}</td>
                    <td>{order.riderId?.fullName || order.riderId?.firstName || 'Unassigned'}</td>
                    <td>
                      <Badge style={{
                        backgroundColor: order.isPaid ? colors.green :
                          order.isDelivered ? colors.blue :
                            order.isDispatched ? colors.amber : colors.line,
                        color: colors.white
                      }}>
                        {order.isDelivered ? 'Delivered' :
                          order.isDispatched ? 'Dispatched' :
                            order.isPaid ? 'Paid' : 'Pending'}
                      </Badge>
                    </td>
                    <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td>UGX {(order.totalPrice || 0).toLocaleString()}</td>
                    <td>
                      {order.riderId && ['ASSIGNED', 'ACCEPTED'].includes(order.delivery_status) && (
                        <Button
                          size="sm"
                          variant="outline-warning"
                          disabled={unassigningOrderId === order._id}
                          onClick={() => confirmUnassignOrder(order)}
                        >
                          {unassigningOrderId === order._id ? 'Unassigning…' : 'Unassign'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{ backgroundColor: colors.lineSoft, borderColor: colors.line, color: colors.body }}
            onClick={() => setShowOrdersModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Confirm Modal */}
      <Modal show={confirmModal.show} onHide={hideConfirm} centered>
        <Modal.Header closeButton style={{ background: colors.blue, color: colors.white }}>
          <Modal.Title>{confirmModal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: 'pre-line', fontSize: 14, color: colors.body }}>
          {confirmModal.message}
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            style={{ backgroundColor: colors.lineSoft, borderColor: colors.line, color: colors.body }}
            onClick={hideConfirm}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            style={{ backgroundColor: colors.blue, borderColor: colors.blue, color: colors.white }}
            onClick={confirmModal.onConfirm}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PartnerDetails;
